<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { callAPI, DRUPAL_API_ROUTE } from '@/service/DrupalService';
import Popover from 'primevue/popover';

const { t, locale } = useI18n();

const popover = ref();
const items = ref([]);
const unread = ref(0);
const loading = ref(false);

const detailVisible = ref(false);
const detailItem = ref(null);

const fetchNotifications = async () => {
  loading.value = true;
  try {
    const response = await callAPI(DRUPAL_API_ROUTE.NOTIFICATIONS, { locale: locale.value });
    items.value = response?.data?.items || [];
    unread.value = response?.data?.unread || 0;
  } catch (e) {
    items.value = [];
    unread.value = 0;
  } finally {
    loading.value = false;
  }
};

const markAllRead = async () => {
  if (unread.value === 0) return;
  try {
    await callAPI(DRUPAL_API_ROUTE.NOTIFICATIONS_READ, { locale: locale.value });
    items.value = items.value.map(item => ({ ...item, read: true }));
    unread.value = 0;
  } catch (e) {
    // silent fail
  }
};

const toggle = async (event) => {
  popover.value.toggle(event);
  await markAllRead();
};

const openDetail = (item) => {
  detailItem.value = item;
  detailVisible.value = true;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale.value, {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  } catch (e) {
    return iso;
  }
};

onMounted(fetchNotifications);
</script>

<template>
  <button type="button" class="layout-topbar-action" @click="toggle" v-tooltip.bottom="t('notifications.title')"
    :aria-label="t('notifications.title')">
    <OverlayBadge v-if="unread > 0" :value="unread" severity="danger" size="small" pt:pcBadge:root:class="!inline-flex">
      <i class="pi pi-bell"></i>
    </OverlayBadge>
    <i v-else class="pi pi-bell"></i>
  </button>

  <Popover ref="popover" :pt="{ content: { class: '!p-0' } }">
    <div class="w-[22rem] max-w-full">
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <span class="font-semibold">{{ t('notifications.title') }}</span>
      </div>

      <div v-if="loading" class="p-6 flex items-center justify-center">
        <ProgressSpinner style="width:2rem;height:2rem" />
      </div>

      <div v-else-if="items.length === 0" class="p-6 text-center text-sm text-gray-500">
        {{ t('notifications.empty') }}
      </div>

      <ul v-else class="max-h-[28rem] overflow-y-auto m-0 p-0 list-none">
        <li v-for="item in items" :key="item.uuid" @click="openDetail(item)"
          class="cursor-pointer px-4 py-3 border-b last:border-b-0 border-gray-100 hover:bg-gray-50">
          <div class="font-medium text-sm break-words">{{ item.title }}</div>
          <div class="text-xs text-gray-400 mt-1">{{ formatDate(item.created) }}</div>
        </li>
      </ul>
    </div>
  </Popover>

  <Dialog v-model:visible="detailVisible" modal :header="detailItem?.title" :style="{ width: '32rem' }"
    :dismissableMask="true">
    <div v-if="detailItem?.body" class="text-sm" v-html="detailItem.body"></div>
    <div v-if="detailItem?.created" class="text-xs text-gray-400 mt-4">{{ formatDate(detailItem.created) }}</div>
  </Dialog>
</template>
